import React from 'react'
import { forwardRef, ReactNode, useRef, MouseEvent } from 'react'
import classNames from 'classnames'

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> {
    color?: string
    active?: boolean
    size?: 'lg' | 'md' | 'sm'
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void
    children?: ReactNode
    className?: string
    loading?: boolean
    /** Accessible label for the loading state. Defaults to "Loading" */
    loadingLabel?: string
    disabled?: boolean
    outline?: boolean
    ghost?: boolean
    block?: boolean
    pill?: boolean
    upload?: boolean|'multiple'
    onUpload?: (files: File[]) => void
    fileTypes?: string
    style?: React.CSSProperties
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
    color = 'secondary',
    active,
    size,
    onClick,
    children,
    className,
    loading,
    loadingLabel = 'Loading',
    disabled,
    outline,
    ghost,
    block,
    pill,
    upload,
    onUpload,
    fileTypes,
    style,
    'aria-label': ariaLabel,
    ...otherProps
}, ref) => {

    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files === null) {
            return
        }

        const files = Array.from(event.target.files)

        // detect onChange for same file
        event.target.value = ''

        if (onUpload) onUpload(files)
    }

    const handleButtonClick = () => {
        fileInputRef.current?.click()
    }

    const computedClassName = classNames(
        'btn',
        {
            [`btn-${color}`]: !outline && !ghost && color,
            [`btn-outline-${color}`]: outline,
            [`btn-ghost-${color}`]: ghost,
            [`btn-${size}`]: size && size !== 'md',
            'btn-pill': pill,
            'btn-block': block,
            'btn-disabled': disabled,
            'active': active
        },
        className
    )

    const isDisabled = disabled || loading

    return (
        <>
            <button
                ref={ref}
                type="button"
                className={computedClassName}
                onClick={!isDisabled ? (upload ? handleButtonClick : onClick) : undefined}
                disabled={isDisabled}
                aria-disabled={isDisabled}
                aria-busy={loading}
                aria-label={ariaLabel}
                style={style}
                {...otherProps}
            >
                <span
                    className={classNames('d-inline-block text-start')}
                    style={{
                        overflowX: 'hidden',
                        verticalAlign: 'bottom',
                        width: loading ? '1.5rem' : 0,
                        opacity: loading ? 1 : 0,
                        transition: 'width 0.2s ease, opacity 0.2s ease'
                    }}
                    aria-hidden="true"
                >
                    <span
                        className={classNames('spinner-border spinner-border-sm')}
                        aria-hidden="true"
                    />
                </span>
                {loading && (
                    <span className="visually-hidden" role="status">
                        {loadingLabel}
                    </span>
                )}
                {children}
            </button>

            {/* if an upload button, add a hidden input and have button simulate a click on this input */}
            {upload &&
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    multiple={upload === 'multiple'}
                    accept={fileTypes}
                    style={{ display: 'none' }}
                    aria-hidden="true"
                    tabIndex={-1}
                />
            }
        </>
    )
})

Button.displayName = 'Button'

export { Button }
